import API_CONFIG from '../../config/api.config';

/**
 * Kiosk (public terminal) QR authentication — see
 * dezshop-operations/docs/kiosk-qr-auth.md.
 *
 * The shared warehouse PC has no username/password login: it shows a QR, the
 * employee approves it in one tap from their already-logged-in phone, and the
 * terminal runs as that person until 30s of inactivity locks it.
 *
 * The device and session credentials are both HttpOnly cookies, so nothing here
 * touches a token directly — every call just needs `credentials: 'include'`.
 * The one thing JS does keep is a non-secret marker in localStorage saying
 * "this browser is a terminal", because it has to be readable *synchronously*:
 * authService decides which refresh endpoint to use before any fetch can
 * resolve. The marker is re-set on every boot by /kiosk/start, which matters
 * because the terminal's Chrome profile is wiped at each startup.
 */

const KIOSK_MODE_KEY = 'kiosk_mode';
const KIOSK_TERMINAL_KEY = 'kiosk_terminal_name';
const KIOSK_TYPE_KEY = 'kiosk_terminal_type';
export const PACKING_TERMINAL_PATH = '/terminal/packing';
export function isPackingTerminal(): boolean {
  try { return isKioskMode() && localStorage.getItem(KIOSK_TYPE_KEY) === 'packing'; } catch { return false; }
}
export function kioskHome(): string { return isPackingTerminal() ? PACKING_TERMINAL_PATH : '/dashboard'; }

export const KIOSK_ENDPOINTS = {
  ENROLL: '/stoc/auth/kiosk/enroll',
  TERMINAL: '/stoc/auth/kiosk/terminal',
  PAIR_START: '/stoc/auth/kiosk/pair/start',
  PAIR_INFO: '/stoc/auth/kiosk/pair/info',
  PAIR_POLL: '/stoc/auth/kiosk/pair/poll',
  PAIR_APPROVE: '/stoc/auth/kiosk/pair/approve',
  PAIR_REJECT: '/stoc/auth/kiosk/pair/reject',
  TOKEN: '/stoc/auth/kiosk/token',
  HEARTBEAT: '/stoc/auth/kiosk/heartbeat',
  LOCK: '/stoc/auth/kiosk/lock',
} as const;

export interface TerminalInfo {
  terminal_id: number;
  name: string;
  idle_timeout_seconds: number;
  terminal_type?: 'crm' | 'packing';
  home_path?: string;
}

export interface PairStart {
  code: string;
  code_suffix: string;
  expires_in: number;
  poll_interval: number;
  terminal: TerminalInfo;
}

export interface PairInfo {
  terminal_name: string;
  code_suffix: string;
  expires_in: number;
  status: string;
}

function url(path: string): string {
  return `${API_CONFIG.BASE_URL}${path}`;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(url(path), {
    method: 'POST',
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    // Statusul e purtat pe eroare: apelantul trebuie să deosebească un 429
    // („alt tab a cerut deja un cod, așteaptă") de o defecțiune reală, altfel
    // ar arăta omului o eroare pentru o situație normală.
    const error = new Error((data as any)?.detail || `Kiosk request failed (${response.status})`);
    (error as any).status = response.status;
    throw error;
  }
  return data as T;
}

/** Synchronous, so authService can branch before its first fetch. */
export function isKioskMode(): boolean {
  try {
    return localStorage.getItem(KIOSK_MODE_KEY) === '1';
  } catch {
    return false;
  }
}

export function getKioskTerminalName(): string | null {
  try {
    return localStorage.getItem(KIOSK_TERMINAL_KEY);
  } catch {
    return null;
  }
}

function setKioskMarker(terminal: TerminalInfo | null): void {
  try {
    if (terminal) {
      localStorage.setItem(KIOSK_MODE_KEY, '1');
      localStorage.setItem(KIOSK_TERMINAL_KEY, terminal.name);
      localStorage.setItem(KIOSK_TYPE_KEY, terminal.terminal_type || 'crm');
    } else {
      localStorage.removeItem(KIOSK_MODE_KEY);
      localStorage.removeItem(KIOSK_TERMINAL_KEY);
      localStorage.removeItem(KIOSK_TYPE_KEY);
    }
  } catch {
    /* private mode / storage disabled — kiosk mode simply won't stick */
  }
}

/**
 * Called once per browser launch by /kiosk/start with the token that
 * start-kiosk.bat injected into the URL fragment.
 */
export async function enroll(deviceToken: string): Promise<TerminalInfo> {
  const terminal = await post<TerminalInfo>(KIOSK_ENDPOINTS.ENROLL, { device_token: deviceToken });
  setKioskMarker(terminal);
  return terminal;
}

/**
 * Confirms with the server that this browser really is an enrolled terminal.
 * Returns null (and clears the marker) if the terminal was revoked, so a
 * decommissioned PC falls back to the normal login screen instead of getting
 * stuck on a QR that can never be approved.
 */
export async function getTerminal(): Promise<TerminalInfo | null> {
  try {
    const response = await fetch(url(KIOSK_ENDPOINTS.TERMINAL), { credentials: 'include' });
    if (!response.ok) {
      if (response.status === 401) setKioskMarker(null);
      return null;
    }
    const terminal = (await response.json()) as TerminalInfo;
    setKioskMarker(terminal);
    return terminal;
  } catch {
    // Network failure: keep the marker (fail-closed — an offline terminal stays
    // a terminal and stays locked) but report "unknown".
    return null;
  }
}

export function pairStart(): Promise<PairStart> {
  return post<PairStart>(KIOSK_ENDPOINTS.PAIR_START);
}

/**
 * Long-poll: resolves as soon as the phone taps Autorizează, or after the
 * server's poll window with `{status: 'pending'}`.
 */
export async function pairPoll(code: string): Promise<{ status: string }> {
  const controller = new AbortController();
  // The server holds a normal poll for 25 seconds. A lost connection must
  // eventually return control to the QR retry loop, including during JSON read.
  const timeout = setTimeout(() => controller.abort(), 35000);
  try {
    const response = await fetch(`${url(KIOSK_ENDPOINTS.PAIR_POLL)}?code=${encodeURIComponent(code)}`, {
      credentials: 'include',
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Kiosk polling failed (${response.status})`);
    return (await response.json()) as { status: string };
  } finally {
    clearTimeout(timeout);
  }
}

/** Unauthenticated on purpose: the phone may still be at its login screen. */
export async function pairInfo(code: string): Promise<PairInfo> {
  const response = await fetch(`${url(KIOSK_ENDPOINTS.PAIR_INFO)}?code=${encodeURIComponent(code)}`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((data as any)?.detail || 'Cod inexistent');
  return data as PairInfo;
}

/** From the phone, authenticated as the person being logged in. */
export async function approve(code: string, accessToken: string): Promise<{ terminal_name: string }> {
  const response = await fetch(url(KIOSK_ENDPOINTS.PAIR_APPROVE), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ code }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((data as any)?.detail || 'Autorizarea a eșuat');
  return data as { terminal_name: string };
}

export async function reject(code: string, accessToken: string): Promise<void> {
  await fetch(url(KIOSK_ENDPOINTS.PAIR_REJECT), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ code }),
  });
}

/** Only the visible tab sends this, so N tabs don't mean N heartbeats. */
export async function heartbeat(): Promise<boolean> {
  try {
    const response = await fetch(url(KIOSK_ENDPOINTS.HEARTBEAT), {
      method: 'POST',
      credentials: 'include',
    });
    return response.ok;
  } catch {
    return false;
  }
}

/** Idempotent — whichever tab reaches zero first closes the session. */
export async function lock(reason: 'idle' | 'manual' = 'manual'): Promise<void> {
  try {
    await fetch(`${url(KIOSK_ENDPOINTS.LOCK)}?reason=${reason}`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    /* the session dies on its own within 60s anyway */
  }
}

export default {
  isKioskMode,
  getKioskTerminalName,
  enroll,
  getTerminal,
  pairStart,
  pairPoll,
  pairInfo,
  approve,
  reject,
  heartbeat,
  lock,
};
